import clsx from 'clsx';
import { forwardRef, ComponentPropsWithRef } from 'react';
import { useMatch, Link, LinkProps } from 'react-router-dom';
import { styled, useStyletron } from '@tigergraph/app-ui-lib/Theme';

type NavLinkProps = LinkProps & { to: string };

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(({ to, className, ...props }, ref) => {
  const match = useMatch(to);
  const [css, theme] = useStyletron();
  return (
    <Link
      {...props}
      to={to}
      ref={ref}
      className={clsx(
        css({
          backgroundColor: match ? theme.colors['navigation.background.selected'] : 'transparent',
          fontWeight: match ? '700' : '500',
          ':hover': {
            backgroundColor: theme.colors['navigation.background.hover'],
          },

          borderLeft: `2px solid ${match ? theme.colors['navigation.bolder.selected'] : 'transparent'}`,
          padding: '12px 8px 8px 22px',
          // currently, we do not distinguish between active and non active states
          color: 'inherit',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }),
        className
      )}
    />
  );
});

export const NavLinkLevel2 = forwardRef<HTMLAnchorElement, NavLinkProps>(({ className, ...props }, ref) => {
  const [css] = useStyletron();
  return (
    <NavLink
      {...props}
      ref={ref}
      className={clsx(
        css({
          padding: '8px 12px 8px 46px',
        }),
        className
      )}
    />
  );
});

export const NavLinkLevel3 = forwardRef<HTMLAnchorElement, NavLinkProps>(({ className, ...props }, ref) => {
  const [css] = useStyletron();
  return (
    <NavLink
      {...props}
      ref={ref}
      className={clsx(
        css({
          padding: '4px 4px 4px 86px',
        }),
        className
      )}
    />
  );
});

export const NavLinkLevelPopover = forwardRef<HTMLAnchorElement, NavLinkProps>(({ className, ...props }, ref) => {
  const [css] = useStyletron();
  return (
    <NavLink
      {...props}
      ref={ref}
      className={clsx(
        css({
          padding: '8px 16px 8px 14px',
          backgroundColor: 'transparent',
          borderLeftColor: 'transparent !important',
        }),
        className
      )}
    />
  );
});

export const NavOl = styled('ol', {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'grid',
  rowGap: '4px',
});

export const NavLi = styled('li', {
  width: '100%',
  overflow: 'hidden',
  flexShrink: 0,
});

export const NavIcon = forwardRef<HTMLAnchorElement, NavLinkProps>(({ className, to, ...props }, ref) => {
  const [css, theme] = useStyletron();
  const match = useMatch(to);
  return (
    <span
      {...props}
      ref={ref}
      className={css({
        width: '24px',
        fontSize: '24px',
        color: match ? theme.colors['navigation.icon.selected'] : 'inherit',
      })}
    />
  );
});

export const NavIconLevel3 = styled('span', {
  width: '32px',
  fontSize: '24px',
});

type NavLabelProps = ComponentPropsWithRef<'span'> & { $match: boolean };

export const NavLabel = styled<'span', NavLabelProps>('span', ({ $match, $theme }) => {
  return {
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
});

export const NavLabelLevel3 = styled<'span', NavLabelProps>('span', ({ $match }) => {
  return {
    fontSize: '12px',
    marginRight: 'auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '170px',
  };
});
